import { useEffect, useState } from 'react';
import { createMessageListener, sendMessage } from 'src/react/_sw/Messenger';
import { canUseServiceWorker, registerValidSW, swUrl } from 'src/react/_sw/serviceWorkerRegistration';
import { useInactivityTimer } from './_hooks/useInactivityTimer';
import { useIsApplicationUpdateLocked } from './_hooks/useIsApplicationUpdateLocked';

const CHECK_FOR_UPDATE_INTERVAL = 15 * 60 * 1000; // 15 minutes
const RUN_UPDATE_TASK_INTERVAL = 4 * 60 * 1000; // 4 minutes
const START_COUNTDOWN_VALUE = 15;
const INACTIVITY_BEFORE_COUNTDOWN_MS = 60 * 1000; // 1 minute

const LEFT_OFFSET = 16;

type UpdateStatus = 'current' | 'available';

type Props = {
  sideBarRef: HTMLDivElement | null;
};

export const UpdateChecker = ({ sideBarRef }: Props): React.JSX.Element | null => {
  const [updateStatus, setUpdateStatus] = useState<UpdateStatus>('current');
  const [sideBarWidth, setSideBarWidth] = useState(0);

  const { isUpdateLocked } = useIsApplicationUpdateLocked();

  const canUpdate = updateStatus === 'available' && !isUpdateLocked;

  const { countdown } = useInactivityTimer({
    isActive: canUpdate,
    inactivityBeforeCountdownMs: INACTIVITY_BEFORE_COUNTDOWN_MS,
    startCountdownValue: START_COUNTDOWN_VALUE,
    onCountdownEnded: skipWaitingSw,
  });

  useEffect(() => {
    let lastUpdateTimestamp = performance.now();
    const runCheck = () => {
      if (performance.now() - lastUpdateTimestamp < CHECK_FOR_UPDATE_INTERVAL) {
        return;
      }
      checkIfUpdateExist();
      lastUpdateTimestamp = performance.now();
    };

    const listenerCleanup = createMessageListener('update_ready', () => {
      lastUpdateTimestamp = performance.now();
      setUpdateStatus('available');
    });

    runCheck();
    const intervalId = setInterval(runCheck, RUN_UPDATE_TASK_INTERVAL);
    return (): void => {
      clearInterval(intervalId);
      listenerCleanup();
    };
  }, []);

  useEffect(() => {
    if (!canUpdate) return;

    function onVisibilityChange() {
      if (document.hidden) skipWaitingSw();
    }

    onVisibilityChange();
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [canUpdate]);

  useEffect(() => {
    if (!sideBarRef) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSideBarWidth(0);
      return;
    }

    const updateWidth = () => setSideBarWidth(sideBarRef.getBoundingClientRect().width);

    updateWidth();

    const observer = new ResizeObserver(updateWidth);

    observer.observe(sideBarRef);

    return () => {
      observer.disconnect();
    };
  }, [sideBarRef]);

  return countdown ? (
    <div style={{ left: sideBarWidth + LEFT_OFFSET }} className={`update-checker tiny-text`}>
      An update is available. Reloading page in {countdown}s...
    </div>
  ) : null;
};

async function skipWaitingSw(): Promise<void> {
  const registration = await navigator.serviceWorker.register(swUrl);

  if (registration?.waiting) {
    registration.waiting.postMessage({ type: 'SKIP_WAITING' });

    await registration.update();
  }

  window.location.reload();
}

function checkIfUpdateExist() {
  if (!canUseServiceWorker()) return;
  registerValidSW(swUrl, { onUpdate: () => sendMessage('update_ready', null) });
}
